import styled, { css } from 'styled-components';
import breakpoints from 'base/constants/breakpoints';
import { colors, Typography } from 'syngenta-digital-cropwise-react-ui-kit';

export const SectionWrapper = styled.div`
  background-color: #ffffff;
  padding: 32px 0;
  @media screen and ${breakpoints.maxWidth.sm} {
    padding: 24px 16px;
  }
`;

export const SectionTitle = styled(Typography.Title)`
  font-style: normal;
  font-weight: 400;
  font-size: 19px;
  line-height: 28px;
  letter-spacing: -0.0125em;
  color: ${colors.neutral100};
  margin-bottom: 8px;

  @media screen and ${breakpoints.maxWidth.sm} {
    font-family: Noto Sans;
    font-size: 16px !important;
    font-weight: 550;
    line-height: 24px;
    letter-spacing: -0.0075em;
  }
`;

export const SectionDescription = styled(Typography.Text)`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${colors.neutral60};
  display: block;
  margin-bottom: 24px;

  @media screen and ${breakpoints.maxWidth.sm} {
    font-size: 14px;
    line-height: 20px;
  }
`;

export const ContentPlaceholder = styled.div`
  border: 1px dashed ${colors.neutral30};
  border-radius: 16px;
  padding: 48px 32px;
  text-align: center;
  background-color: #f8fafe;
  color: ${colors.neutral60};
  font-size: 14px;
  line-height: 22px;

  @media screen and ${breakpoints.maxWidth.sm} {
    border-radius: 0;
    border-left: none;
    border-right: none;
    padding: 32px 16px;
  }
`;

// ── Color maps ────────────────────────────────────────────────────────────────
const CELL_BG: Record<string, string> = {
  green: '#C6EFCE',
  blue: '#BDD7EE',
};

const CELL_TEXT: Record<string, string> = {
  green: '#276221',
  blue: '#1A4A7A',
};

// Highlights the table column whose period contains the report's planting date.
const PLANTING_DATE_HIGHLIGHT = '#0071cd';

// ── Meta info row ─────────────────────────────────────────────────────────────
export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

export const MetaChip = styled.span`
  background: #f0f4f8;
  border: 1px solid #dfe2e7;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  color: #3c4149;
  font-weight: 500;
`;

// ── Table ─────────────────────────────────────────────────────────────────────
export const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  margin-bottom: 16px;

  @media screen and ${breakpoints.maxWidth.sm} {
    display: none;
  }
`;

export const SeedRateTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: auto;

  @media screen and ${breakpoints.maxWidth.sm} {
    font-size: 12px;
  }
`;

export const MonthGroupHeaderCell = styled.th`
  background: #29303b;
  border: 1px solid #1c2229;
  padding: 8px 10px;
  text-align: center;
  font-weight: 600;
  color: #ffffff;
  font-size: 13px;
  min-width: 90px;

  @media screen and ${breakpoints.maxWidth.sm} {
    padding: 6px 6px;
    font-size: 11px;
    min-width: 66px;
  }
`;

export const PeriodSubHeader = styled.th<{ $isPlantingPeriod?: boolean }>`
  background: #f5f7fa;
  border: ${({ $isPlantingPeriod }) =>
    ($isPlantingPeriod ? `2px solid ${PLANTING_DATE_HIGHLIGHT}` : '1px solid #dfe2e7')};
  border-bottom: ${({ $isPlantingPeriod }) =>
    ($isPlantingPeriod ? `none` : '1px solid #dfe2e7')};
  padding: 6px 8px;
  text-align: center;
  font-weight: 600;
  color: #3c4149;
  font-size: 12px;
  min-width: 44px;

  @media screen and ${breakpoints.maxWidth.sm} {
    padding: 4px 4px;
    font-size: 11px;
    min-width: 36px;
  }
`;

export const PeriodCell = styled.td<{
  $cellColor: 'green' | 'blue' | null;
  $isPlantingPeriod?: boolean;
}>`
  border: ${({ $isPlantingPeriod }) =>
    ($isPlantingPeriod ? `2px solid ${PLANTING_DATE_HIGHLIGHT}` : '1px solid #dfe2e7')};
  border-top: ${({ $isPlantingPeriod }) =>
    ($isPlantingPeriod ? `none` : '1px solid #dfe2e7')};
  padding: 10px 8px;
  text-align: center;
  font-size: 13px;
  min-width: 44px;
  font-weight: ${({ $cellColor }) => ($cellColor ? 600 : 400)};
  background-color: ${({ $cellColor }) => ($cellColor ? CELL_BG[$cellColor] : 'transparent')};
  color: ${({ $cellColor }) => ($cellColor ? CELL_TEXT[$cellColor] : '#8c94a0')};

  @media screen and ${breakpoints.maxWidth.sm} {
    padding: 8px 4px;
    font-size: 11px;
    min-width: 36px;
  }
`;

// ── Legend ────────────────────────────────────────────────────────────────────
export const LegendRow = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #3c4149;
`;

export const LegendSwatch = styled.span<{ $dotColor: 'green' | 'blue' }>`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background-color: ${({ $dotColor }) => CELL_BG[$dotColor]};
  border: 1px solid ${({ $dotColor }) => CELL_TEXT[$dotColor]};
`;

// ── Mobile card grid (visible only on mobile) ─────────────────────────────────
export const MobileMonthGrid = styled.div`
  display: none;

  @media screen and ${breakpoints.maxWidth.sm} {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }
`;

export const MobileMonthCard = styled.div`
  flex: 0 0 100%;
  border: 1px solid #dfe2e7;
  border-radius: 4px;
  overflow: hidden;
`;

export const MobileMonthCardHeader = styled.div`
  background: #29303b;
  color: #ffffff;
  text-align: center;
  font-weight: 600;
  font-size: 11px;
  padding: 6px 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MobileMonthCardPeriods = styled.div`
  display: flex;
`;

export const MobileMonthPeriodCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #dfe2e7;
  &:first-child {
    border-left: none;
  }
`;

export const MobileMonthPeriodHeader = styled.div<{ $isPlantingPeriod?: boolean }>`
  background: #f5f7fa;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #3c4149;
  padding: 4px 2px;
  border-bottom: 1px solid #dfe2e7;
  ${({ $isPlantingPeriod }) =>
    $isPlantingPeriod &&
    css`
      border: 2px solid ${PLANTING_DATE_HIGHLIGHT};
      border-bottom: none;
    `}
`;

export const MobileMonthPeriodCell = styled.div<{
  $cellColor: 'green' | 'blue' | null;
  $isPlantingPeriod?: boolean;
}>`
  text-align: center;
  font-size: 11px;
  font-weight: ${({ $cellColor }) => ($cellColor ? 600 : 400)};
  background-color: ${({ $cellColor }) => ($cellColor ? CELL_BG[$cellColor] : 'transparent')};
  color: ${({ $cellColor }) => ($cellColor ? CELL_TEXT[$cellColor] : '#8c94a0')};
  padding: 8px 2px;
  ${({ $isPlantingPeriod }) =>
    $isPlantingPeriod &&
    css`
      border: 2px solid ${PLANTING_DATE_HIGHLIGHT};
      border-top: none;
    `}
`;
